import React, { useContext } from 'react';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  standardColours,
  brandColours,
  fontWeights,
  fontSize,
  visuallyHidden,
  standardTransition,
} from '../styles';
import { Svg, Link } from './ui';
import { StoreContext } from '../context/StoreContext';
import heartIcon from '../images/heart-icon.svg';

const StyledWishlistToggle = styled.div`
  position: relative;
`;

const StyledButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  height: 42px;
  width: 42px;
  background-color: ${({ alt, altTheme }) =>
    alt || altTheme ? standardColours.transparent : brandColours.primary};
  border-radius: 50%;

  ${({ alt, altTheme }) => {
    if (alt) {
      return css`
        border: 1px solid ${brandColours.primary};
      `;
    } else if (altTheme) {
      return css`
        border: 1px solid ${standardColours.white};
      `;
    }
  }}
`;

const StyledIcon = styled(Svg)`
  height: 20px;
  width: 20px;
  fill: ${standardColours.transparent};
  stroke: ${({ alt }) => (alt ? brandColours.primary : standardColours.white)};
  transition: ${standardTransition('fill')};

  ${({ active }) => {
    if (active) {
      return css`
        fill: ${({ alt }) =>
          alt ? brandColours.primary : standardColours.white};
      `;
    }
  }};
`;

const StyledText = styled.span`
  ${visuallyHidden()};
`;

const StyledLink = styled(Link)`
  position: absolute;
  padding: 8px;
  color: ${standardColours.white};
  font-weight: ${fontWeights.bold};
  ${fontSize(13)};
  line-height: 1.4;
  text-align: center;
  background-color: ${brandColours.primary};
  z-index: 1;

  ${minBreakpointQuery.smedium`
    white-space: nowrap;
  `}

  ${minBreakpointQuery.large`
    padding: 10px;
    ${fontSize(14)};
  `}

  &:before {
    content: '';
    position: absolute;
    height: 0;
    width: 0;
  }

  ${({ alt, altTheme }) => {
    if (alt || altTheme) {
      return css`
        top: 56px;
        left: 50%;
        transform: translateX(-50%);

        &:before {
          top: 0;
          left: 50%;
          border-right: 10px solid ${standardColours.transparent};
          border-bottom: 10px solid ${brandColours.primary};
          border-left: 10px solid ${standardColours.transparent};
          transform: translateX(-50%) translateY(-100%);
        }
      `;
    } else {
      return css`
        top: 50%;
        left: 56px;
        transform: translateY(-50%);

        &:before {
          top: 50%;
          left: 0;
          border-top: 10px solid ${standardColours.transparent};
          border-right: 10px solid ${brandColours.primary};
          border-bottom: 10px solid ${standardColours.transparent};
          transform: translateX(-100%) translateY(-50%);
        }
      `;
    }
  }};
`;

const WishlistToggle = ({ spa, alt, altTheme, ...props }) => {
  const { isSpaInWishlist, toggleSpaInWishlist } = useContext(StoreContext);

  const active = isSpaInWishlist(spa);

  return (
    <StyledWishlistToggle {...props}>
      <StyledButton
        alt={alt}
        altTheme={altTheme}
        onClick={() => toggleSpaInWishlist(spa)}
      >
        <StyledIcon
          image={heartIcon}
          alt={alt}
          altTheme={altTheme}
          active={active}
        />
        <StyledText>Add to wishlist</StyledText>
      </StyledButton>
      {active && (
        <StyledLink to="/spa-short-list" alt={alt} altTheme={altTheme}>
          View favourites
        </StyledLink>
      )}
    </StyledWishlistToggle>
  );
};

export default WishlistToggle;
